import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { toast } from "@/shared/lib/toast";
import { Button } from "@/shared/ui/button";
import { Modal } from "@/shared/ui/modal";

import type { AttentionReason } from "../api/use-orders";
import { useResolveReason, useReviewChoices } from "../api/use-review";
import type { Order } from "../lib/types";

interface ResolveDialogProps {
  open: boolean;
  order: Order;
  reason: AttentionReason;
  onClose: () => void;
}

/** Record-only decision dialog: never touches stock or lifecycle, just clears the flag. */
export function ResolveDialog({
  open,
  order,
  reason,
  onClose,
}: ResolveDialogProps): JSX.Element | null {
  const { t, i18n } = useTranslation("orders");
  const { data: choicesByReason } = useReviewChoices();
  const resolveReason = useResolveReason();
  const [choice, setChoice] = useState<string | null>(null);
  const [note, setNote] = useState("");

  if (!open) return null;

  const choices = choicesByReason?.[reason] ?? [];
  const isOther = choice === "other";
  const canConfirm = choice !== null && (!isOther || note.trim().length > 0);

  function handleConfirm(): void {
    if (!choice || (isOther && !note.trim())) return;
    resolveReason.mutate(
      { id: order.id, reason, choice, ...(note.trim() ? { note: note.trim() } : {}) },
      { onSuccess: onClose, onError: () => toast.error(t("resolve.fail")) },
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t("resolve.title", { reason: t(`reason.${reason}`) })}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="resolve-keep">
            {t("common:action.cancel")}
          </Button>
          <Button
            variant="default"
            size="sm"
            disabled={!canConfirm || resolveReason.isPending}
            onClick={handleConfirm}
            data-testid="resolve-confirm"
          >
            {t("resolve.confirm")}
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm" data-testid="resolve-dialog">
        <div>
          <div className="mb-1.5 font-medium">{t("resolve.question")}</div>
          <div className="space-y-2" role="radiogroup" aria-label={t("resolve.question")}>
            {choices.map((c) => (
              <label key={c.code} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="resolve-choice"
                  value={c.code}
                  checked={choice === c.code}
                  onChange={() => setChoice(c.code)}
                  data-testid={`resolve-choice-${c.code}`}
                />
                <span>{i18n.language === "th" ? c.label_th : c.label_en}</span>
              </label>
            ))}
          </div>
        </div>

        {isOther && (
          <div>
            <label htmlFor="resolve-note" className="mb-1.5 block font-medium">
              {t("resolve.other_note")}
            </label>
            <textarea
              id="resolve-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              rows={3}
              required
              data-testid="resolve-note"
              className="w-full rounded-lg border border-[var(--premium-border)] px-3 py-2 text-sm"
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
